import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { migrateAnonymousChat } from '../services/chatService';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  // Load user profile function
  const loadUserProfile = async (userId) => {
    if (!userId) return;
    setProfileLoading(true);
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();
      if (!error && data) {
        setUserProfile(data);
      }
    } catch (error) {
      console.error('Profile load error:', error);
    } finally {
      setProfileLoading(false);
    }
  };

  // Clear user profile function
  const clearUserProfile = () => {
    setUserProfile(null);
    setProfileLoading(false);
  };

  // Auth state change handler
  const handleAuthStateChange = (event, session) => {
    const currentUser = session?.user ?? null;
    setUser(currentUser);
    setLoading(false);

    if (currentUser) {
      loadUserProfile(currentUser?.id);
    } else {
      clearUserProfile();
    }
  };

  useEffect(() => {
    // Initial session check with error handling
    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await supabase?.auth?.getSession();

        // Handle invalid refresh token error
        if (error && error?.message?.includes('Invalid Refresh Token')) {
          console.warn('Invalid refresh token detected, clearing session');
          // Clear the invalid session from storage
          await supabase?.auth?.signOut({ scope: 'local' });
          handleAuthStateChange(null, null);
          return;
        }

        // Handle other errors
        if (error) {
          console.error('Session initialization error:', error);
          handleAuthStateChange(null, null);
          return;
        }

        // Valid session found
        handleAuthStateChange(null, session);
      } catch (error) {
        console.error('Auth initialization failed:', error);
        // Clear potentially corrupted session
        try {
          await supabase?.auth?.signOut({ scope: 'local' });
        } catch (signOutError) {
          console.error('Failed to clear corrupted session:', signOutError);
        }
        handleAuthStateChange(null, null);
      }
    };

    initializeAuth();

    // Subscribe to auth state changes with error handling
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((event, session) => {
      // Handle specific auth events
      if (event === 'TOKEN_REFRESHED') {
        console.log('Token refreshed successfully');
      } else if (event === 'SIGNED_OUT') {
        console.log('User signed out');
      }

      handleAuthStateChange(event, session);
    });

    return () => {
      if (subscription) {
        subscription?.unsubscribe();
      }
    };
  }, []);

  // Auth methods
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });

      if (!error && data?.user) {
        await migrateAnonymousChat(data.user.id);
      }

      return { data, error };
    } catch (error) {
      console.error('Sign in error:', error);
      return { error: { message: 'Network error. Please try again.' } };
    }
  };

  const signOut = async () => {
    try {
      // Use 'local' scope to only clear client-side session
      const { error } = await supabase?.auth?.signOut({ scope: 'local' });
      if (!error) {
        setUser(null);
        clearUserProfile();
      }
      return { error };
    } catch (error) {
      console.error('Sign out error:', error);
      // Force clear local state even if API call fails
      setUser(null);
      clearUserProfile();
      return { error: { message: 'Network error. Please try again.' } };
    }
  };

  const signUp = async (email, password, fullName) => {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            role: 'user'
          }
        }
      });

      if (!error && data?.user) {
        await migrateAnonymousChat(data.user.id);
      }

      return { data, error };
    } catch (error) {
      console.error('Sign up error:', error);
      return { error: { message: 'Network error. Please try again.' } };
    }
  };

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } };

    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single();

      if (!error && data) {
        setUserProfile(data);
        // Reload profile to get fresh data
        await loadUserProfile(user?.id);
      }
      return { data, error };
    } catch (error) {
      console.error('Update profile error:', error);
      return { error: { message: 'Network error. Please try again.' } };
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signUp,
    signOut,
    updateProfile,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};